import { useGet, usePost } from "@/apiHooks/HttpFunctions";
import {
  adminListDataApiEndPoint,
  contactSupportsApi,
  downloadMediaApi,
} from "@/apis/apis";
import Button from "@/components/Button";
import { Col, Row } from "react-bootstrap";
import { FileIcon, defaultStyles } from "react-file-icon";
import { diffForHumans, formatDateTime } from "@/utils/helper";
import { Loader } from "@/components/Loader";
import { useCallback, useState } from "react";
import FilePreviewModal from "@/components/FilePreviewModal/FilePreviewModal";
import axiosInstance from "@/Axios/ConfiguredAxios";
import RenderToast from "@/components/RenderToast";
import { useModalInfo } from "@/context/ModalContext";
import { useFormik } from "formik";
import UserContactSupportSchema from "@/schema/CommunicationMGMT/ContactSupport/user-schema";
import UserContactSupportValues from "@/data/CommunicationMGMT/ContactSupport/user-intial-values";
import { LOADING_PLACEHOLDER } from "@/constant/constants";
import AsyncDropdown from "@/components/AsyncDropdown/AsyncDropdown";
import { LIST_MODE, LIST_PER_PAGE } from "@/constant/Lists/listMode";
import useModalGetOptions from "@/CustomHooks/Modal/useModalGetOptions";
import { CONTACT_SUPPORT_TYPE } from "@/constant/ContactSupport/status";
import config from "@/config";

export default function SupportTicketDetail({ id }) {
  const isEdit = !!id;
  const showApi = `${contactSupportsApi}/${id}`;
  const [modalFileURL, setModalFileURL] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [fileType, setFileType] = useState(null);
  const { openModalInfo } = useModalInfo();

  // GET DATA
  const {
    data: showData,
    isFetching: showIsFetching,
    // error: showError,
  } = useGet({
    apiURL: showApi,
    enabled: true,
    refetchOnWindowFocus: true,
    refetchOnReconnect: true,
    refetchInterval: true,
  });

  const data = showData?.data;

  // FORMIK
  const formik = useFormik({
    enableReinitialize: true,
    initialValues: UserContactSupportValues(isEdit ? showData : null),
    validationSchema: UserContactSupportSchema(),
    onSubmit: (values) => handleSubmit(values),
  });

  // ADMIN DROPDOWN VALUE
  const defaultAdminLabelFormatter = useCallback((item) => item.first_name, []);
  const baseQueryParams = `?mode=${LIST_MODE.SELECT}&perPage=${LIST_PER_PAGE}&filter[keyword]=`;
  const adminListApi = `${adminListDataApiEndPoint}${baseQueryParams}`;
  const {
    loading: adminLoading,
    defaultOptions: adminDefaultOptions,
    debouncedFetchOptions: adminFetchOptions,
    getSelectedOption: adminGetSelectedOption,
  } = useModalGetOptions({
    modalState: { isOpen: !!id },
    listApi: adminListApi,
    formikValue: formik.values.assigned_to_id,
    formikInitialValue: formik.initialValues.assigned_to_id,
    showData: !!id && data?.assigned_to,
    labelFormatter: defaultAdminLabelFormatter,
  });

  // HANDLE SUBMIT FORM
  const handleSubmit = (values) => {
    const formData = new FormData();
    formData.append("admin_comment", values.admin_comment);
    saveComment(formData);
  };

  const handleSubmitAssignedTo = (values) => {
    const formData = new FormData();
    formData.append("assigned_to_id", values.assigned_to_id);
    saveAssignedTo(formData);
  };

  // RENDER TOAST
  const handleSuccess = (text) => (data) => {
    RenderToast({
      type: "success",
      message: text,
    });
  };

  // SAVE ADD COMMENT
  const {
    mutate: saveComment,
    isPending: saveCommentIsPending,
    error: saveCommentError,
  } = usePost({
    isFormData: true,
    apiURL: `${contactSupportsApi}/${id}/add-comment`,
    onSuccess: handleSuccess("Comment added successfully."),
  });

  // SAVE ASSIGNED TO
  const {
    mutate: saveAssignedTo,
    isPending: saveAssignedToIsPending,
    error: saveAssignedToError,
  } = usePost({
    isFormData: true,
    apiURL: `${contactSupportsApi}/${id}/assigned-to`,
    onSuccess: handleSuccess("The Ticket has been assigned successfully."),
  });

  // MODAL PREVIEW
  const handleOpenModal = (fileURL, type) => {
    setModalFileURL(fileURL);
    setFileType(type);

    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setTimeout(() => setModalFileURL(null), setFileType(null), 100);
  };
  // MODAL PREVIEW

  // HANDLE DOWNLOAD FILE
  const handleDownload = async (fileName) => {
    try {
      const response = await axiosInstance.get(
        `${downloadMediaApi}/${fileName}`,
        {
          responseType: "blob",
        }
      );
      const blob = response.data;
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = fileName;
      document.body.appendChild(a);
      a.click();
      a.remove();

      openModalInfo("File downloaded successfully.");
    } catch (error) {
      RenderToast({
        type: "error",
        message: "Something went wrong.",
      });
    }
  };
  // HANDLE DOWNLOAD FILE

  if (showIsFetching) {
    return (
      <div className="z-[100]">
        <Loader />
      </div>
    );
  }

  return (
    <div className={showIsFetching ? "filter blur-sm" : ""}>
      {/* Status and Details */}
      <Row className="font-semibold">
        <Col md={2}>Status</Col>
        <Col md={2}>P-Index</Col>
        <Col md={2}>Reason</Col>
        <Col md={2}>Start Date</Col>
        <Col md={2}>Started</Col>
        <Col md={2}>Latest Message</Col>
      </Row>
      <Row className="mb-2">
        <Col md={2}>{`${data?.status.name} - ${data?.status.type}`}</Col>
        <Col md={2}>{data?.reason.type}</Col>
        <Col md={2}>{data?.reason.name}</Col>
        <Col md={2}>{formatDateTime(data?.created_at, true)}</Col>
        <Col md={2} className="capitalize">
          {diffForHumans(data?.created_at) ?? "-"}
        </Col>
        <Col md={2} className="capitalize">
          {diffForHumans(data?.updated_at) ?? "-"}
        </Col>
      </Row>

      {/* Assigned Details */}
      <Row className="font-semibold">
        <Col md={2}>Latest Message By</Col>
        <Col md={2}>Assigned To</Col>
        <Col md={2}>Assigned By</Col>
        <Col md={2}>Creator User</Col>
        <Col md={2}>Creator First Name</Col>
        <Col md={2}>Ticket Transaction-ID</Col>
      </Row>
      <Row>
        {/* Assigned To Dropdown */}
        <Col md={2}>
          <span>-</span>
        </Col>
        <Col md={2}>
          {
            <AsyncDropdown
              isDisabled={
                saveAssignedToIsPending ||
                data?.status?.type === CONTACT_SUPPORT_TYPE.CLOSED
              }
              required={false}
              zIndex={0}
              fontSize="13px"
              height={24}
              containerClassName="!grid-cols-5 max-w-[200px] !gap-0"
              dropdownWrapperClassName="!col-span-5 !ml-0"
              placeholder="Unassigned"
              defaultOptions={adminDefaultOptions}
              loadOptions={adminFetchOptions}
              isLoading={adminLoading || saveAssignedToIsPending}
              value={adminGetSelectedOption()}
              onChange={(selected) => {
                formik.setFieldValue("assigned_to_id", selected?.value || null);
                handleSubmitAssignedTo({
                  assigned_to_id: selected?.value || null,
                });
              }}
              errorText={
                (formik.touched.assigned_to_id &&
                  formik.errors.assigned_to_id) ||
                (saveAssignedToError?.status === 422 &&
                  saveAssignedToError?.response?.data?.errors?.assigned_to_id?.join(
                    " "
                  ))
              }
            />
          }
        </Col>
        {/* Assigned To Dropdown */}
        <Col md={2}>{data?.assigned_by?.first_name ?? "-"}</Col>
        <Col md={2}>{data?.creatorable.username ?? "-"}</Col>
        <Col md={2}>{data?.creatorable.first_name}</Col>
        <Col md={2}>{data?.code}</Col>

      </Row>

      <Row className="font-semibold mt-3">
        <Col md={2}>Reference Username</Col>
        <Col md={2}>Reference URL</Col>
        <Col md={2}>Reference Transaction-ID </Col>

      </Row>

      <Row>
        <Col md={2}>
          {(data.user_reference && (
            <a
              href={`${config.app.baseUrl}/user-management/web-user-list?filter[keyword]=${data.user_reference?.username}`}
              className="underline text-blue-800"
              target="_blank"
              rel="noreferrer"
            >
              {data?.user_reference?.username}
            </a>
          )) ||
            "-"}
        </Col>
        <Col md={2}>
          {(data?.url_reference && (
            <a
              href={data.url_reference}
              className="underline text-blue-800"
              target="_blank"
              rel="noreferrer"
            >
              {data?.url_reference}
            </a>
          )) ||
            "-"}
        </Col>
        <Col md={2}>
          {(data?.transaction_reference && (
            <a
              href={data.transaction_reference?.code ?? "#"}
              className="underline text-blue-800"
              target="_blank"
              rel="noreferrer"
            >
              {data?.transaction_reference?.code}
            </a>
          )) ||
            "-"}
        </Col>
      </Row>

      {/* Admin Comments */}
      <Row className="mt-3">
        <Col md={8}>
          <label className="font-semibold">Admin Comment</label>
        </Col>
      </Row>
      <Row className="mb-4">
        <Col md={8}>
          <form
            onSubmit={formik.handleSubmit}
            className="flex flex-row items-center gap-2"
          >
            <textarea
              value={formik.values.admin_comment}
              onChange={(e) =>
                formik.setFieldValue("admin_comment", e.target.value)
              }
              className="border rounded-md w-full p-2 h-10"
              placeholder="Type Comment..."
            ></textarea>
            <Button
              label={saveCommentIsPending ? LOADING_PLACEHOLDER : "Save"}
              type="submit"
              className="!w-[100px] !text-[14px] bg-excel-gradient text-white"
              disabled={saveCommentIsPending}
            />
          </form>
          {saveCommentError && (
            <div className="mt-2 text-[red]">{saveCommentError}*</div>
          )}
        </Col>
      </Row>

      {/* Actions */}
      <Row className="mb-2">
        <div className="flex gap-2">
          <Button
            variant="green-gradient"
            label={"Send Message"}
            onClick={() => alert("Send Message")}
            disabled={!data.assigned_to}
            type="button"
            className="!w-[120px] !text-[14px]"
          />
          <Button
            variant="green-gradient"
            label={"Send Email"}
            onClick={() => alert("Send Email")}
            disabled={!data.assigned_to}
            type="button"
            className="!w-[120px] !text-[14px]"
          />
        </div>
      </Row>

      {/* Attachments */}
      <Row className="mb-3">
        {Object.values(data?.attachments || {}).map((attachment, index) => (
          <Col md={4} key={attachment.file_name}>
            <div className="flex flex-row mt-2">
              <span className="font-semibold">Attachment {index + 1}</span>
            </div>
            <span
              onClick={(e) => {
                handleDownload(attachment.file_name);
              }}
              className="hover:underline hover:text-blue-500 cursor-pointer"
            >
              {attachment.file_name}
            </span>
            <div
              className="w-[1.5rem] mt-2 cursor-pointer "
              onClick={(e) => {
                if (!isModalOpen) {
                  handleOpenModal(
                    attachment.original_url,
                    attachment.extension
                  );
                }
              }}
            >
              <FileIcon
                extension={attachment.extension}
                {...defaultStyles[attachment.extension]}
                radius={4}
              />
              {isModalOpen && modalFileURL && (
                <FilePreviewModal
                  onClose={handleCloseModal}
                  fileURL={modalFileURL}
                  type={fileType}
                />
              )}
            </div>
          </Col>
        ))}
      </Row>

      {/* Message Thread */}
      <Row className="mt-4">
        <Col md={12}>
          <span className="font-semibold">Message Thread</span>
          <p className="border rounded-md p-2 h-[100px]">{data?.message}</p>
        </Col>
      </Row>
    </div>
  );
}
